import React, { useEffect, useState } from 'react';
import { AlertTriangle, Calculator, CalendarClock, CheckCircle2, FilePlus2, Printer, Save, Search, Send, ShieldAlert, Users } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import { DateInput, Select } from '../../components/Controls';
import { FileChip, FilePicker } from '../../components/Files';
import { LEAVE_ATTACHMENT_RULE } from '../../fileRules';
import { esc } from '../attendance/shared';
import { saveRequest } from './ApplyForm';
import { STATUS_LABEL, longDate, num, typeId } from './shared';

// Advanced Dashboard > Leave Request (Stephanet's leavemgmt/LeaveRequestForm.jsx): Calculate Days (calendar / working days, balance before
// and after, expected resumption), Check Conflict (own overlapping leave, department colleagues off, holidays), Print Form, Save Draft, Submit

const BLANK = { employee_id: '', leave_type: '', start_date: '', end_date: '', reason: '', reliever_id: '', emergency_contact: '' };

export default function LeaveRequestForm({ data, reload }) {
  const { notify } = useHR();
  const [f, setF] = useState(BLANK);
  const [file, setFile] = useState(null);
  const [calc, setCalc] = useState(null);
  const [showConflict, setShowConflict] = useState(false);
  const [busy, setBusy] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  useEffect(() => { setCalc(null); setShowConflict(false); }, [f.employee_id, f.leave_type, f.start_date, f.end_date]);

  const emp = data.employees.find(e => String(e.id) === String(f.employee_id));
  const types = data.types.filter(t => t.active !== false && (!emp || !t.gender || t.gender === 'All' || t.gender === emp.gender));
  const type = data.types.find(t => t.name === f.leave_type);
  const bal = emp && type?.has_balance ? emp.balances[f.leave_type] : null;
  const datesOk = f.start_date && f.end_date && f.end_date >= f.start_date;
  const ready = emp && type && datesOk && f.reason.trim();

  const check = async conflict => {
    if (!emp || !type || !datesOk) { notify('Choose the employee, leave type and both dates first', 'bad'); return null; }
    setBusy(conflict ? 'conflict' : 'calc');
    try {
      const r = await hr.checkLeave({ employee_id: emp.id, leave_type_id: typeId(data, type.name), start_date: f.start_date, end_date: f.end_date });
      setCalc(r);
      if (conflict) { setShowConflict(true); notify(r.ok && !r.warnings.length ? 'No conflicts found' : r.ok ? `${r.warnings.length} thing(s) to note` : `${r.issues.length} conflict(s) found`, r.ok ? undefined : 'bad'); }
      return r;
    } catch (e) { notify(e.message, 'bad'); return null; } finally { setBusy(''); }
  };
  const save = async how => {
    if (!ready) return;
    if (how === 'submit' && type.attachment === 'Required' && !file) { notify(`${type.name} leave needs a supporting document`, 'bad'); return; }
    setBusy(how);
    try {
      const r = await saveRequest(data, { ...f, handover_note: null }, how, file, notify);
      notify(r.message || r.reference);
      setF(BLANK); setFile(null); setCalc(null); setShowConflict(false);
      await reload();
    } catch (e) { notify(e.message, 'bad'); }
    setBusy('');
  };

  const printForm = async () => {
    if (!emp || !type) { notify('Choose the employee and leave type first', 'bad'); return; }
    const c = calc || (datesOk ? await check(false) : null);
    const reliever = data.employees.find(e => String(e.id) === String(f.reliever_id));
    const row = (k, v) => `<tr><th>${esc(k)}</th><td>${esc(v) || '&nbsp;'}</td></tr>`;
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    document.body.appendChild(frame);
    frame.contentDocument.write(`<!doctype html><html><head><title>Leave Application — ${esc(emp.name)}</title><style>
      body{font-family:Arial,sans-serif;margin:32px;color:#0f172a} h1{margin:0;font-size:20px;color:#0b318b} h2{font-size:13px;margin:22px 0 6px;color:#0b318b;text-transform:uppercase;letter-spacing:.05em}
      .sub{color:#475569;font-size:12px;margin:4px 0 16px} table{width:100%;border-collapse:collapse;font-size:13px} th,td{border:1px solid #cbd5e1;padding:7px 10px;text-align:left;vertical-align:top}
      th{width:34%;background:#f1f5f9} .sig{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;margin-top:36px;font-size:12px} .sig div{border-top:1px solid #0f172a;padding-top:6px}
    </style></head><body>
      <h1>Leave Application Form</h1><div class="sub">Printed ${esc(new Date().toLocaleString('en-GB'))}</div>
      <h2>Employee</h2><table>${row('Employee', `${emp.name} (${emp.employee_no})`)}${row('Department', emp.department)}${row('Grade', emp.grade)}${row('Project Site', emp.site)}${row('Reporting Manager', emp.manager)}</table>
      <h2>Leave</h2><table>${row('Leave Type', `${type.name} Leave (${type.paid ? 'Paid' : 'Unpaid'})`)}${row('Start Date', longDate(f.start_date))}${row('End Date', longDate(f.end_date))}
        ${c ? row('Calendar Days', c.calendar_days) + row('Working Days', c.working_days) + row('Balance Before', c.balance_before ?? 'Not tracked') + row('Balance After', c.balance_after ?? 'Not tracked') + row('Expected Resumption', `${longDate(c.resumption)} (${c.resumption_day})`) : ''}
        ${row('Reason', f.reason)}${row('Handover Officer', reliever ? `${reliever.name} (${reliever.employee_no})` : '')}${row('Contact Phone', f.emergency_contact)}</table>
      <div class="sig"><div>Employee signature &amp; date</div><div>Supervisor approval</div><div>HR approval</div></div></body></html>`);
    frame.contentDocument.close();
    setTimeout(() => { frame.contentWindow.focus(); frame.contentWindow.print(); setTimeout(() => frame.remove(), 1000); }, 200);
  };

  return (
    <form className="mdr-card lvm-form lvm-nlr" onSubmit={e => { e.preventDefault(); save('submit'); }}>
      <div className="lvm-nlr-title"><FilePlus2 size={16} /> NEW LEAVE REQUEST</div>
      <div className="lvm-grid two">
        <label>Employee
          <Select value={f.employee_id} onChange={e => set('employee_id', e.target.value)} required>
            <option value="">Select employee...</option>
            {data.employees.map(e => <option key={e.id} value={e.id}>{`${e.name} (${e.employee_no})`}</option>)}
          </Select>
          {emp && <span className="lvm-under">Dept: <b>{emp.department || '—'}</b>{emp.grade ? <> · Grade: <b>{emp.grade}</b></> : null}</span>}
        </label>
        <label>Leave Type
          <Select value={f.leave_type} onChange={e => set('leave_type', e.target.value)} required>
            <option value="">Select type...</option>
            {types.map(t => <option key={t.id} value={t.name}>{`${t.name} Leave`}</option>)}
          </Select>
          {type && <span className="lvm-info">Entitlement: <b>{bal ? num(bal.entitled + bal.carried + bal.adjusted) : type.days || '—'} days</b> | Paid: <b>{type.paid === false ? 'No' : 'Yes'}</b> | Attachment: <b>{type.attachment}</b></span>}
          {type && emp && (bal ? <span className={`lvm-curbal ${bal.available <= 0 ? 'bad' : ''}`}>Current Balance: <b>{num(bal.available)} days</b></span>
            : <span className="lvm-curbal na">Balance not tracked for {type.name} leave</span>)}
        </label>
        <label>Start Date<DateInput value={f.start_date} onChange={e => set('start_date', e.target.value)} required /></label>
        <label>End Date<DateInput value={f.end_date} onChange={e => set('end_date', e.target.value)} required /></label>
      </div>
      <button type="button" className="mds-run lvm-calc-btn" disabled={busy === 'calc'} onClick={() => check(false)}><Calculator size={14} /> {busy === 'calc' ? 'Calculating…' : 'Calculate Days'}</button>

      {calc && (
        <>
          <div className="lvm-calc lvm-calc4">
            <div><small>Calendar Days</small><b>{calc.calendar_days}</b><span>{calc.weekend_days} weekend day(s)</span></div>
            <div className="ok"><small>Working Days</small><b>{calc.working_days}</b><span>{calc.holidays.length ? `${calc.holidays.length} holiday(s) excluded` : 'weekends excluded'}</span></div>
            <div><small>Balance Before</small><b>{calc.tracked ? num(calc.balance_before) : '—'}</b><span>{calc.tracked ? 'available days' : 'not tracked'}</span></div>
            <div className={calc.tracked && calc.balance_after < 0 ? 'bad' : ''}><small>Balance After</small><b>{calc.tracked ? num(calc.balance_after) : '—'}</b><span>{calc.tracked && calc.balance_after < 0 ? 'not enough balance' : 'after this leave'}</span></div>
          </div>
          <div className="lvm-resume"><CalendarClock size={15} /> Expected Resumption Date: <b>{longDate(calc.resumption)} ({calc.resumption_day})</b></div>
          {calc.holidays.length > 0 && <div className="lvm-hint">Holidays in this period: {calc.holidays.map(h => `${h.name} (${longDate(h.date)})`).join(', ')}</div>}
        </>
      )}

      {showConflict && calc && (
        <div className={`lvm-conflict ${calc.ok ? (calc.warnings.length ? 'warn' : 'ok') : 'bad'}`}>
          <div className="lvm-conflict-head">{calc.ok ? (calc.warnings.length ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />) : <ShieldAlert size={16} />}
            <b>{calc.ok ? (calc.warnings.length ? 'No blocking conflicts — please note' : 'No conflicts found') : `${calc.issues.length} conflict(s) — this request would be refused`}</b></div>
          {calc.issues.map(x => <p key={x} className="bad">• {x}</p>)}
          {calc.warnings.map(x => <p key={x}>• {x}</p>)}
          {calc.team_on_leave.length > 0 && (
            <div className="lvm-team"><span><Users size={13} /> Same department off in this period ({calc.team_on_leave.length} of {calc.department_size} staff):</span>
              <ul>{calc.team_on_leave.map((t, i) => <li key={i}><b>{t.name}</b> — {t.leave_type}, {longDate(t.start_date)} → {longDate(t.end_date)} <em>({STATUS_LABEL[t.status] || t.status})</em></li>)}</ul>
            </div>
          )}
        </div>
      )}

      <div className="lvm-grid two">
        <label className="span3">Reason for Leave<textarea rows={3} value={f.reason} onChange={e => set('reason', e.target.value)} required placeholder="Reason for the leave…" /></label>
        <label>Handover Officer
          <Select value={f.reliever_id} onChange={e => set('reliever_id', e.target.value)}>
            <option value="">Select handover officer...</option>
            {data.employees.filter(e => String(e.id) !== String(f.employee_id)).map(e => <option key={e.id} value={e.id}>{`${e.name} (${e.employee_no})`}</option>)}
          </Select>
        </label>
        <label>Contact Phone<input type="tel" value={f.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} placeholder="Phone number during leave" /></label>
        <div className="span3 lvm-attach">
          <span className="lvm-label">Attach Document{type?.attachment === 'Required' ? ' *' : ''}</span>
          {file ? <FileChip name={file.name} size={file.size} pending onRemove={() => setFile(null)} />
            : <FilePicker rule={LEAVE_ATTACHMENT_RULE} onPick={setFile} label="Click to attach" onError={m => notify(m, 'bad')} />}
        </div>
      </div>

      <div className="mdh-form-foot lvm-nlr-foot">
        <button type="button" className="mdr-btn" disabled={!ready || !!busy} onClick={() => save('draft')}><Save size={14} /> {busy === 'draft' ? 'Saving…' : 'Save Draft'}</button>
        <button type="submit" className="mds-run" disabled={!ready || !!busy}><Send size={14} /> {busy === 'submit' ? 'Submitting…' : 'Submit Request'}</button>
        <button type="button" className="mdr-btn" disabled={!emp || !type} onClick={printForm}><Printer size={14} /> Print Form</button>
        <button type="button" className="mdr-btn lvm-conflict-btn" disabled={!!busy} onClick={() => check(true)}><Search size={14} /> {busy === 'conflict' ? 'Checking…' : 'Check Conflict'}</button>
      </div>
    </form>
  );
}
